/**
 * keyboardSlice — tracks letter states for the on-screen keyboard.
 * Each letter can be: unused, absent, present, or correct.
 * Once a letter is marked 'correct', it stays correct.
 * Priority: correct > present > absent > unused.
 */
import { createSlice } from '@reduxjs/toolkit';
import { TILE_STATE, TURKISH_ALPHABET } from '../../utils/constants';

// Build initial keyboard state — all letters unused
const buildInitialState = () => {
  const letterStates = {};
  TURKISH_ALPHABET.forEach((letter) => {
    letterStates[letter] = 'unused';
  });
  return { letterStates };
};

const STATE_PRIORITY = {
  unused: 0,
  [TILE_STATE.ABSENT]: 1,
  [TILE_STATE.PRESENT]: 2,
  [TILE_STATE.CORRECT]: 3,
};

const keyboardSlice = createSlice({
  name: 'keyboard',
  initialState: buildInitialState(),
  reducers: {
    /**
     * Update letter states after a guess is evaluated.
     * payload: { guess: string, evaluation: TILE_STATE[] }
     */
    updateLetterStates(state, action) {
      const { guess, evaluation } = action.payload;
      const letters = [...guess];
      letters.forEach((letter, i) => {
        const newState = evaluation[i];
        const currentState = state.letterStates[letter] || 'unused';
        // Only upgrade, never downgrade
        if ((STATE_PRIORITY[newState] || 0) > (STATE_PRIORITY[currentState] || 0)) {
          state.letterStates[letter] = newState;
        }
      });
    },

    /**
     * Reset all keyboard letter states.
     */
    resetKeyboard() {
      return buildInitialState();
    },

    /**
     * Restore keyboard state (from localStorage).
     */
    restoreKeyboard(state, action) {
      state.letterStates = action.payload;
    },
  },
});

export const { updateLetterStates, resetKeyboard, restoreKeyboard } =
  keyboardSlice.actions;

export default keyboardSlice.reducer;
